import React, { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";

/**
 * Shared layout for legal pages (Terms, Privacy, Refunds).
 * Provides navy hero, table of contents with anchor links, and
 * a readable 800px content column.
 */
export function LegalLayout({ title, lastUpdated, sections, children, testId, seoTitle, seoDescription }) {
  useEffect(() => {
    // Smooth scroll for in-page anchor links.
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = "smooth";
    return () => { html.style.scrollBehavior = prev; };
  }, []);

  return (
    <div data-testid={testId} className="min-h-screen bg-white flex flex-col">
      <SEO
        title={seoTitle || `${title} | SchengenFile`}
        description={seoDescription || `${title} for SchengenFile — Schengen visa document preparation service by Trip360 Travel & Tourism Co., Riyadh, Saudi Arabia.`}
      />
      <Header />

      {/* Navy hero */}
      <section className="bg-[#0f1b2d] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.24em] text-[#60a5fa] mb-3">Legal</div>
          <h1 data-testid="legal-page-title" className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl">
            {title}
          </h1>
          <p className="mt-4 text-sm text-white/70">
            Last updated: <span data-testid="legal-last-updated" className="text-white/90 font-medium">{lastUpdated}</span>
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-14">
          {/* Table of contents */}
          <nav
            data-testid="legal-toc"
            aria-label="Table of contents"
            className="border border-[#e2e8f0] rounded-xl bg-[#f8fafc] p-6 mb-12"
          >
            <div className="text-xs uppercase tracking-[0.2em] text-[#64748b] font-semibold mb-3">
              Table of contents
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-[#0f1b2d]">
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    data-testid={`legal-toc-link-${s.id}`}
                    className="text-[#2563eb] hover:underline"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {/* Sections */}
          <article className="legal-prose text-[#1e293b] leading-relaxed text-[15px]">
            {children}
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export function LegalSection({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-12">
      <h2 className="font-display font-extrabold text-2xl text-[#0f1b2d] mb-4">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}
